import { useApolloClient } from '@apollo/client';
import {
  ListNotificationsDocument,
  ListNotificationsQuery,
  NotificationFieldsFragmentDoc,
  useOnNotificationDeleteSubscription,
  useOnNotificationUpdateSubscription,
} from '@graphql';
import { useSession, useToastError } from '@hooks';

export function useNotificationSubscription() {
  const { account } = useSession();
  const toastError = useToastError();
  const client = useApolloClient();

  useOnNotificationUpdateSubscription({
    skip: !account,
    onError: toastError,
    onData: ({ data }) => {
      const notificationUpdate = data.data?.notificationUpdate;
      if (!notificationUpdate) return;

      client.cache.updateQuery<ListNotificationsQuery>({ query: ListNotificationsDocument }, (prev) => {
        const prevList = prev?.notifications ?? [];
        const idx = prevList.findIndex((n) => n.id === notificationUpdate.id);

        if (idx === -1) {
          return { notifications: [notificationUpdate, ...prevList] };
        }

        const next = prevList.slice();
        next[idx] = notificationUpdate;
        return { notifications: next };
      });

      // Optional: ensure normalized entity is written too (useful if other queries read the fragment)
      client.cache.writeFragment({
        fragment: NotificationFieldsFragmentDoc,
        data: notificationUpdate,
      });
    },
  });

  useOnNotificationDeleteSubscription({
    skip: !account,
    onError: toastError,
    onData: ({ data }) => {
      const notificationId = data.data?.notificationDelete;
      if (!notificationId) return;

      client.cache.updateQuery<ListNotificationsQuery>({ query: ListNotificationsDocument }, (prev) => {
        const prevList = prev?.notifications ?? [];
        return { notifications: prevList.filter((n) => n.id !== notificationId) };
      });

      client.cache.evict({ id: client.cache.identify({ __typename: 'Notification', id: notificationId }) });
      client.cache.gc();
    },
  });

  return null;
}
